import { createFileRoute } from '@tanstack/react-router';
import clx from 'classnames';
import _ from 'lodash';
import React from 'react';
import { twMerge } from 'tailwind-merge';
import { match } from 'ts-pattern';
import TablerIconCircleCheckFilled from '~icons/tabler/circle-check-filled';
import TablerIconInfoCircle from '~icons/tabler/info-circle';
import TablerIconRocket from '~icons/tabler/rocket';

import {
  Button,
  ButtonGroup,
  Card,
  Divider,
  // Slider,
  Stack,
  Tooltip,
  Typography,
} from 'gooey';
import { GlowCard } from '#/components/design';
import { ButtonLink } from '#/components/links';
import { Page, PageSection, PageSectionTitle } from '#/components/pages';
import { CustomerLogoList, FrequentlyAskedQuestions } from '#/components/widgets';
import fx from '#/fx';
import { Content, type ExtractLoaderData, pageSEO } from '#/lib/content';
import { canonicalLinkTag } from '#/lib/head';
import { cn } from '#/utils/classnames';

export const Route = createFileRoute('/_main/pricing')({
  loader: async () => {
    const page = await load();
    return { page };
  },
  head: ({ loaderData, match }) => ({
    meta: pageSEO(
      {
        title: 'Pricing',
        description: 'Choose the right plan for your needs.',
        keywords: ['pricing', 'plans', 'pricing plans', 'pricing page', 'pricing options'],
      },
      match,
      loaderData?.page
    ),
    links: [canonicalLinkTag(match.pathname)],
  }),
  component: PricingPage,
});

async function load() {
  const page = await fx.getPageContent({
    data: {
      key: 'pricing',
    },
  });

  const customerLogos = await fx.listCustomerLogos({
    data: {
      fields: {
        ids: page.sections.customers.logos,
      },
    },
  });

  return {
    ...page,
    sections: {
      ...page.sections,
      customers: {
        ...page.sections.customers,
        logos: customerLogos,
      },
    },
  };
}

type LoaderData = ExtractLoaderData<typeof load>;

function PricingPage() {
  const { page } = Route.useLoaderData();
  return (
    <Page gutter={4}>
      <PageSectionHero sections={page.sections} />
      <PageSectionPricing sections={page.sections} />
      <PageSectionCustomerLogos sections={page.sections} />
      <PageSectionFAQs sections={page.sections} />
      <PageSectionStartups sections={page.sections} />
    </Page>
  );
}

function PageSectionHero({ sections }: PricingPageSectionProps) {
  return (
    <PageSection disableGutters className='pb-8 pt-24 md:pt-36'>
      <Stack spacing={2} justifyContent='center' alignItems='center' className='text-center'>
        <Typography variant='h1'>{sections.hero.title}</Typography>
        <Typography component='p' variant='subtitle1'>
          {sections.hero.subtitle}
        </Typography>
      </Stack>
    </PageSection>
  );
}

function PageSectionPricing({ sections }: PricingPageSectionProps) {
  const [
    activePlan,
    // setActivePlan
  ] = React.useState<PricingPlanId | null>(null);
  const [
    totalDevs,
    // setTotalDevs
  ] = React.useState(5);
  const [invoiceFrequency, setInvoiceFrequency] = React.useState<'monthly' | 'annual'>('annual');

  /*
  |------------------
  | Computed
  |------------------
  */

  const compact = React.useMemo(() => {
    return sections.pricing.plans.length > 3;
  }, [sections.pricing.plans]);

  const showEnterprise = React.useMemo(() => {
    return sections.pricing.plans.every((plan) => plan.price !== false);
  }, [sections.pricing.plans]);

  /*
  |------------------
  | Callbacks
  |------------------
  */

  // function updateActivePlan(value: number) {
  //   const recommendedPlan = getRecommendedPlanId(sections.pricing.slider.options, value);

  //   // Force to "enterprise" if the value is greater than the last plan
  //   const lastPlan = sections.pricing.plans[sections.pricing.plans.length - 1].id;
  //   setActivePlan(recommendedPlan ?? lastPlan);
  //   setTotalDevs(value);
  // }

  /*
  |------------------
  | Utils
  |------------------
  */

  function getStatus(plan: PricingPlanId) {
    if (activePlan === null) {
      return 'idle';
    }
    if (plan === activePlan) {
      return 'active';
    }
    return 'disable';
  }

  return (
    <PageSection gutterTop={0}>
      <div
        className={clx({
          'xl:w-[115%] xl:-ml-[7.5%]': compact,
        })}
      >
        <ButtonGroup
          aria-label='Monthly or Annual'
          className='mb-4 md:mb-0 mx-auto text-center block'
        >
          <Button
            variant={invoiceFrequency === 'annual' ? 'contained' : 'outlined'}
            onClick={() => setInvoiceFrequency('annual')}
            size='medium'
          >
            Annual
          </Button>
          <Button
            variant={invoiceFrequency === 'monthly' ? 'contained' : 'outlined'}
            onClick={() => setInvoiceFrequency('monthly')}
            size='medium'
          >
            Monthly
          </Button>
        </ButtonGroup>
        {/* <div className='w-full block lg:hidden mt-8 mb-8'>
          <div className='text-center mb-4'>{sections.pricing.slider.title}</div>
          <Stack direction='row' spacing={0} justifyContent='center' alignItems='stretch'>
            <TextField
              type='number'
              defaultValue={totalDevs}
              className='w-1/2 sm:max-w-72 rounded-r-none'
              size='small'
              min={1}
              max={150}
              fullWidth
              onChange={(e) => updateActivePlan(Number(e.target.value))}
            />
            <Button
              className='rounded-l-none pl-4 sm:pl-6 relative'
              variant='contained'
              size='small'
              disabled={activePlan === null}
              endIcon={<TablerIconArrowDown className='hidden sm:block' />}
              onClick={() => {
                if (activePlan) {
                  const el = document.getElementById(activePlan);
                  if (el) {
                    el.scrollIntoView({
                      behavior: 'smooth',
                      block: 'center',
                    });
                  }
                }
              }}
            >
              {activePlan && (
                <span className='absolute top-0 right-0 -mt-1 -mr-1 flex size-3'>
                  <span className='absolute inline-flex h-full w-full animate-[ping_2s_infinite] rounded-full bg-secondary z-0' />
                  <span className='relative inline-flex size-3 rounded-full bg-secondary z-10' />
                </span>
              )}
              Recommended
            </Button>
          </Stack>
        </div> */}
        {/* <div className='w-full px-16 mb-6 hidden lg:block'>
          <Typography variant='h6' component='p' gutterBottom>
            {sections.pricing.slider.title}: <code>{totalDevs}</code>
          </Typography>
          <PricingSlider options={sections.pricing.slider.options} onChange={updateActivePlan} />
        </div> */}
        <Stack
          direction='row'
          spacing={{
            xs: 1,
            sm: 2,
            md: 4,
          }}
          // spacing={1.5}
          className='flex-wrap md:justify-center sm:flex-nowrap lg:px-8 mt-8 md:mt-16'
          justifyContent={{
            xs: 'stretch',
            md: 'stretch',
            lg: 'stretch',
            xl: 'stretch',
          }}
        >
          {sections.pricing.plans.map((plan) => (
            <PricingItem
              {...plan}
              key={plan.title}
              className='w-full md:w-[calc(50%-16px)] lg:w-full'
              price={plan.seats.price}
              totalPrice={calculatePlanPrice(plan, totalDevs)}
              seatsNeeded={totalDevs}
              compact={compact}
              status={getStatus(plan.id)}
              invoiceFrequency={invoiceFrequency}
            />
          ))}
        </Stack>
        {showEnterprise && (
          <PricingCard className='px-6 py-8 mt-8' status={getStatus('enterprise')}>
            <Stack
              direction={{ xs: 'column', lg: 'row' }}
              spacing={{
                xs: 2,
                lg: 0,
              }}
              justifyContent='space-between'
              alignItems='center'
            >
              <Stack>
                <Typography component='h3' variant='h5' className='font-bold'>
                  <Content content={sections.pricing.enterprise.title} />
                </Typography>
                <Typography component='p' variant='subtitle1'>
                  <Content content={sections.pricing.enterprise.subtitle} />
                </Typography>
              </Stack>
              <ButtonLink
                variant='contained'
                to={sections.pricing.enterprise.cta.url}
                size='large'
                className='w-full lg:w-auto'
              >
                {sections.pricing.enterprise.cta.label}
              </ButtonLink>
            </Stack>
          </PricingCard>
        )}
      </div>
    </PageSection>
  );
}

function PageSectionStartups({ sections }: PricingPageSectionProps) {
  return (
    <PageSection>
      <GlowCard border='all' className='text-center'>
        <PageSectionTitle
          icon={<TablerIconRocket />}
          iconAnimation='shake'
          className='mb-4 md:mb-8'
          title={sections.startups.title}
          subtitle={sections.startups.subtitle}
        />
        <ButtonLink
          variant='contained'
          to={sections.startups.cta.url}
          size='large'
          className='normal-case text-2xl w-full sm:w-fit mx-auto'
        >
          {sections.startups.cta.label}
        </ButtonLink>
      </GlowCard>
    </PageSection>
  );
}

function PageSectionCustomerLogos({ sections }: PricingPageSectionProps) {
  return (
    <PageSection>
      <GlowCard border='top'>
        <Typography variant='h5' component='h2' className='text-center mb-12 text-gray-300'>
          {sections.customers.title}
        </Typography>
        <CustomerLogoList customerLogos={sections.customers.logos} opacity={0.9} />
      </GlowCard>
    </PageSection>
  );
}

function PageSectionFAQs({ sections }: PricingPageSectionProps) {
  return (
    <PageSection>
      <Typography component='h2' variant='h3' className='mb-8'>
        {sections.faqs.title}
      </Typography>
      <FrequentlyAskedQuestions questions={sections.faqs.questions} />
    </PageSection>
  );
}

/*
|------------------
| Internals
|------------------
*/

type PricingPageSectionProps = Pick<LoaderData, 'sections'>;

type PricingPlan = LoaderData['sections']['pricing']['plans'][number];

type PricingPlanId = PricingPlan['id'];

// type PricingPlanSliderOption = LoaderData['sections']['pricing']['slider']['options'][number];

/*
|------------------
| Internal Components
|------------------
*/

// function PricingSlider({
//   onChange,
//   options,
// }: {
//   options: PricingPlanSliderOption[];
//   onChange: (value: number) => void;
// }) {
//   const marks = React.useMemo(() => {
//     return _.chain(options).sortBy('order', 'asc').value();
//   }, [options]);
//   return (
//     <Slider
//       aria-label='Restricted values'
//       // defaultValue={marks[0].value}
//       step={1}
//       // valueLabelDisplay="off"
//       max={marks[marks.length - 1].value}
//       min={marks[0].value}
//       marks={marks}
//       onChange={onChange}
//     />
//   );
// }

function PricingItem({
  title,
  cta,
  id,
  price,
  totalPrice,
  invoiceFrequency,
  features,
  seats,
  seatsNeeded,
  status = 'idle',
  compact = false,
  className,
}: PricingPlan & {
  invoiceFrequency: 'monthly' | 'annual';
  status?: 'active' | 'disable' | 'idle';
  seatsNeeded?: number;
  compact?: boolean;
  totalPrice:
    | {
        monthly: number;
        annual: number;
      }
    | false;
  className?: string;
}) {
  const priceText = React.useMemo(() => {
    if (
      !seatsNeeded ||
      price === false ||
      (seatsNeeded && isContactUsPlan({ price, seats }, seatsNeeded))
    ) {
      return {
        title: 'Custom',
        cta: cta.label,
        subPriceText: '(based on your needs & usage)',
        totalTitle: <br />,
      };
    }

    const priceText = match({ ...price, invoiceFrequency })
      .with({ invoiceFrequency: 'monthly' }, (p) => p.monthly)
      .with({ invoiceFrequency: 'annual' }, (p) => p.annual)
      .exhaustive();

    return {
      title: (
        <span>
          ${priceText}
          <span className='text-lg font-normal text-gray-300/90'> /seat</span>
        </span>
      ),
      subPriceText: `(billed ${invoiceFrequency === 'monthly' ? 'monthly' : 'annually'})`,
      totalTitle: totalPrice
        ? `$${invoiceFrequency === 'monthly' ? totalPrice.monthly : totalPrice.annual} / mo`
        : '',
      cta: cta.label,
    };
  }, [price, seats, seatsNeeded, invoiceFrequency, cta, totalPrice]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: ignore for now
  const computedFeatures = React.useMemo(() => {
    if (price === false || seats.price === false) {
      return features;
    }

    // const aleCartPrice = invoiceFrequency === 'monthly' ? seats.price.monthly : seats.price.annual;
    // const extraSeats = seats.max - seats.included;

    return [
      // {
      //   label: `${seats.included} seats included`,
      //   included: true,
      //   tooltip: `You can buy up to ${extraSeats} additional seats for $${aleCartPrice}/month`,
      // },
      ...features,
    ];
  }, [features, price, seats, invoiceFrequency]);

  return (
    <div className={cn('quarks-pricing-item relative w-full', className)} id={id}>
      <PricingCard
        className={cn('w-full overflow-visible', 'px-4 py-4 sm:pt-6 sm:pb-4')}
        status={status}
      >
        <Stack>
          <Typography component='h2' variant='h5' className='text-center'>
            {title}
          </Typography>
          {/* <Typography
            component='p'
            variant='caption'
            className={clx(
              'text-center text-green-500 bg-green-800 bg-opacity-40 rounded-md sm:mt-2 py-0.5 px-2 w-fit mx-auto',
              {
                visible: status === 'active',
                invisible: status !== 'active',
              }
            )}
          >
            recommended
          </Typography> */}
          <Typography
            className={clx('mt-4 sm:mt-6 text-center font-bold', {
              'text-4xl': compact,
              'text-5xl md:text-7xl': !compact,
            })}
          >
            {priceText.title}
          </Typography>
          <Typography
            variant='caption'
            className={clx('block text-center mt-3 mb-5', {
              // invisible: match(price)
              //   .with(P.not(false), (x) => x.monthly <= 0 && x.annual <= 0)
              //   .otherwise(() => true),
            })}
          >
            {priceText.subPriceText || <br />}
          </Typography>
          <Divider />
          <Stack
            spacing={{ xs: 0.5, sm: 1, md: 1.5 }}
            component='ul'
            className='list-none my-4 sm:mt-6 sm:mb-8 p-0'
          >
            {computedFeatures.map((feature) => (
              <Stack
                key={feature.label}
                direction='row'
                spacing={{ xs: 1, sm: 2 }}
                component='li'
                alignItems='center'
                alignContent='center'
              >
                <TablerIconCircleCheckFilled
                  className={twMerge(
                    feature.included ? 'text-green-400' : 'text-gray-400',
                    compact ? 'w-5 h-auto' : 'w-7 h-auto'
                  )}
                />
                <Typography
                  variant='body1'
                  className={twMerge(
                    'flex flex-row items-center justify-center -mr-2',
                    compact ? 'text-lg' : 'text-md md:text-lg lg:text-xl'
                  )}
                >
                  <Content content={feature.label} />
                </Typography>
                {feature.tooltip && (
                  <Tooltip title={feature.tooltip} placement='top'>
                    <TablerIconInfoCircle className='text-gray-400 hover:text-gray-500 cursor-pointer w-4 h-auto md:w-5' />
                  </Tooltip>
                )}
              </Stack>
            ))}
          </Stack>
          <span className='hidden sm:block'>
            <ButtonLink
              variant='contained'
              to={cta.url}
              fullWidth
              size='large'
              className={clx('inline-flex text-2xl', {
                'bg-primary-dark': status === 'disable',
              })}
            >
              {priceText.cta}
            </ButtonLink>
          </span>
          <span className='block sm:hidden'>
            <ButtonLink
              variant='contained'
              to={cta.url}
              fullWidth
              size='small'
              className={clx('inline-flex text-md', {
                'bg-primary-dark': status === 'disable',
              })}
            >
              {priceText.cta}
            </ButtonLink>
          </span>
          <Typography
            variant='caption'
            className={cn('text-center mt-2', { invisible: !cta.infoText })}
          >
            {cta.infoText ?? '-'}
          </Typography>
        </Stack>
      </PricingCard>
    </div>
  );
}

function PricingCard({
  children,
  status = 'idle',
  className,
}: React.PropsWithChildren<{
  className?: string;
  status: 'idle' | 'active' | 'disable';
}>) {
  return (
    <Card
      className={twMerge(
        'h-full bg-black border-solid border-2 border-slate-400',
        className,
        status === 'active' && 'glow-lg glow-primary',
        status === 'disable' && 'border-slate-600 text-gray-400'
      )}
    >
      {children}
    </Card>
  );
}

/*
|------------------
| Utils
|------------------
*/

// /**
//  * Get the recommended plan.
//  *
//  * @param options The pricing plans slider options.
//  * @param seats The current number of seats.
//  */
// function getRecommendedPlanId(options: PricingPlanSliderOption[], seats: number) {
//   return _.chain(options)
//     .sortBy('order', 'asc')
//     .find((option) => seats <= option.value)
//     .get('plan')
//     .value();
// }

/**
 * Is the current plan the contact us plan?
 *
 * @param plan The pricing plan.
 * @param seats The current number of seats.
 */
function isContactUsPlan(plan: Pick<PricingPlan, 'price' | 'seats'>, seats: number) {
  if (plan.price === false) {
    return true;
  }

  return plan.seats.included < seats && plan.seats.max < seats;
}

function calculatePlanPrice(plan: PricingPlan, seats: number) {
  if (plan.price === false || plan.seats.price === false) {
    return false;
  }

  const seatsNeeded = seats; // + plan.seats.included;

  if (seatsNeeded <= 0) {
    return plan.price;
  }

  const monthly = seatsNeeded * plan.seats.price.monthly;
  const annual = seatsNeeded * plan.seats.price.annual;

  return {
    monthly,
    annual,
  };
}
